import { yupResolver } from "@hookform/resolvers/yup"
import { AppBar, Stack, Tab, Tabs, Typography } from "@mui/material"
import { HTMLAttributes, SyntheticEvent, useEffect, useRef, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import {
  METADATA_FORM_SECTIONS,
  METADATA_FORM_SECTION_IDS,
  getDefaultAndSavedValues,
} from "@features/builder/UpdateMetadata/helpers"
import {
  type MetadataFormValues,
  getValidationSchema,
} from "@features/builder/UpdateMetadata/validation"
import { GraphDetails } from "@features/builder/types"
import { updateGraphDocumentData } from "@utils/crdt"
import { useGraphDocument } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocument"
import { MetadataFormDetailsSection } from "@features/builder/UpdateMetadata/MetadataFormDetailsSection"
import { MetadataFormLinksSection } from "@features/builder/UpdateMetadata/MetadataFormLinksSection"
import { MetadataFormScenariosSection } from "@features/builder/UpdateMetadata/MetadataFormScenariosSection"
import { MetadataFormTagsSection } from "@features/builder/UpdateMetadata/MetadataFormTagsSection"
import ConfirmDialog from "@components/ConfirmDialog"
import { useDispatch } from "react-redux"
import { graphsApi } from "@store/services/graphsService"
import MetadataFormOverviewSection from "@features/builder/UpdateMetadata/MetadataFormOverviewSection"

export type MetadataFormProps = Omit<HTMLAttributes<HTMLFormElement>, "onSubmit"> & {
  graphDetails?: string
  graphKey?: string
  scenarios?: string[]
  onSubmit: (values: MetadataFormValues) => void
}

interface ValuesToUpdate {
  ScenarioSource: string
  ScenarioVersion: string
  Scenarios: string[]
}

const MetadataForm = ({
  onSubmit,
  graphDetails,
  graphKey,
  scenarios,
  ...props
}: MetadataFormProps) => {
  const [isDeletingSourceFileDialogOpen, setIsDeletingSourceFileDialogOpen] =
    useState<boolean>(false)
  const formRef = useRef<HTMLFormElement>(null)
  const [activeSection, setActiveSection] = useState<string>(METADATA_FORM_SECTIONS.details)
  const graphDocument = useGraphDocument()
  const graphDetailsData: GraphDetails = graphDetails ? JSON.parse(graphDetails) : {}
  const formMethods = useForm<MetadataFormValues>({
    defaultValues: getDefaultAndSavedValues(graphDetailsData),
    resolver: yupResolver(getValidationSchema()),
  })
  const { handleSubmit, reset, setValue, trigger, getValues } = formMethods

  const dispatch = useDispatch()

  // Reset form when graphDetails changes, but preserve any File the user is currently selecting
  useEffect(() => {
    const currentScenarioSource = getValues("scenario_source")
    const defaultValues = getDefaultAndSavedValues(graphDetails ? JSON.parse(graphDetails) : {})
    if (currentScenarioSource instanceof File) {
      defaultValues.scenario_source = currentScenarioSource
    }
    reset(defaultValues)
  }, [getValues, graphDetails, reset, trigger])

  const handleConfirmDeleteScenarioSource = () => {
    setIsDeletingSourceFileDialogOpen(true)
  }

  const handleDeleteSourceFile = async (isConfirmed: boolean) => {
    setIsDeletingSourceFileDialogOpen(false)
    if (!isConfirmed) return

    const oldScenarioValue = formMethods.getValues("scenario_source")
    setValue("scenario_source", null)

    // wasn't uploaded yet, user selected file and decided to remove it
    if (oldScenarioValue instanceof File) {
      return
    }

    const valuesToUpdate: ValuesToUpdate = {
      ScenarioSource: "",
      ScenarioVersion: "",
      Scenarios: [],
    }
    const newData = Object.assign(graphDetailsData, valuesToUpdate)
    if (graphDocument) updateGraphDocumentData(graphDocument, newData)

    // Invalidate only the scenario-related queries, not all Graph queries
    dispatch(graphsApi.util.invalidateTags(["ScenarioSourceMnemonics", "MnemonicsData"]))
  }

  const handleSectionChange = (_event: SyntheticEvent, sectionId: string) => {
    setActiveSection(sectionId)
    formRef.current
      ?.querySelector(`#${sectionId}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  // Keep the active tab in sync with whichever section is currently scrolled into view.
  useEffect(() => {
    const formEl = formRef.current
    if (!formEl) return

    const sectionEls = METADATA_FORM_SECTION_IDS.map((sectionId) =>
      formEl.querySelector(`#${sectionId}`),
    ).filter((sectionEl): sectionEl is Element => Boolean(sectionEl))

    if (!sectionEls.length) return

    // Find the nearest scrollable ancestor (e.g. the dialog content) to use as the observer root.
    let scrollParent = formEl.parentElement
    while (scrollParent && !/(auto|scroll)/.test(getComputedStyle(scrollParent).overflowY)) {
      scrollParent = scrollParent.parentElement
    }

    // Track the latest known intersection state for every section, since a single observer
    // callback only reports the sections whose intersection changed, not all observed sections.
    const latestEntriesMap = new Map<string, IntersectionObserverEntry>()

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => latestEntriesMap.set(entry.target.id, entry))

        const latestEntries = Array.from(latestEntriesMap.values())
        const visibleEntries = latestEntries.filter((entry) => entry.isIntersecting)
        if (!visibleEntries.length) return

        const topMostEntry = visibleEntries.reduce((closest, entry) =>
          entry.boundingClientRect.top < closest.boundingClientRect.top ? entry : closest,
        )
        setActiveSection(topMostEntry.target.id)
      },
      { root: scrollParent, rootMargin: "-50px 0px -70% 0px", threshold: 0 },
    )

    sectionEls.forEach((sectionEl) => observer.observe(sectionEl))

    return () => observer.disconnect()
  }, [])

  return (
    <FormProvider {...formMethods}>
      <form
        ref={formRef}
        autoComplete="off"
        noValidate
        {...props}
        onSubmit={handleSubmit(onSubmit)}
      >
        <AppBar position="sticky" color="default" elevation={0} sx={{ mb: 2 }}>
          <Tabs value={activeSection} onChange={handleSectionChange}>
            <Tab label="Details" value={METADATA_FORM_SECTIONS.details} />
            <Tab label="Overview" value={METADATA_FORM_SECTIONS.overview} />
            {graphKey && <Tab label="Links" value={METADATA_FORM_SECTIONS.links} />}
            <Tab label="Scenarios" value={METADATA_FORM_SECTIONS.scenarios} />
            <Tab label="Tags" value={METADATA_FORM_SECTIONS.tags} />
          </Tabs>
        </AppBar>

        <Stack direction="column" spacing={2}>
          <MetadataFormDetailsSection graphDetails={graphDetails} />
          <MetadataFormOverviewSection />
          {graphKey && <MetadataFormLinksSection />}
          <MetadataFormScenariosSection
            scenarios={scenarios ?? []}
            onDeleteScenarioSource={handleConfirmDeleteScenarioSource}
          />
          <MetadataFormTagsSection />
        </Stack>
      </form>
      {isDeletingSourceFileDialogOpen && (
        <ConfirmDialog
          open={isDeletingSourceFileDialogOpen}
          dialogTitle="Confirm"
          onClose={handleDeleteSourceFile}
        >
          <Typography>Do you want to remove scenario source file ?</Typography>
        </ConfirmDialog>
      )}
    </FormProvider>
  )
}

export default MetadataForm
